import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useAB } from 'Douleutaras/app/lib/ab';
import { TABLET_BIG } from 'Douleutaras/constants/media-queries';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import { getCountryCode } from 'Douleutaras/hooks/localization';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import { jobSelectors } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { categoriesSelector } from 'Douleutaras/redux/data/categories/categoriesSlice';
import { businessUnitsSelector } from 'Douleutaras/redux/data/businessUnits/businessUnitsSlice';
import { jobMunicipalitySelectors } from 'Douleutaras/redux/data/jobMunicipality/jobMunicipalitySlice';
import { BusinessModel, JobPricingModels } from 'Douleutaras/types';
import { addSnackbarNotification, SnackbarNotificationType } from 'Douleutaras/redux/data/ui/snackbarSlice';
import { PhoneNumberRevealedSource } from 'Douleutaras/app/lib/posthog/posthog.types';

import './PhoneDropdown.scss';

interface PhoneDropdownProps {
  phone: string;
  jobId: string;
  bidId: string;
  sellerId: string;
  source: PhoneNumberRevealedSource;
  children: React.ReactElement<{ onClick?(): void; }>;
  onReveal?(): void;
}

const DROPDOWN_OFFSET_PX = 12;

export const PhoneDropdown = ({ phone, jobId, bidId, sellerId, source, children, onReveal }: PhoneDropdownProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery({ maxWidth: TABLET_BIG.MAX_WIDTH - 1 });
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(undefined);
  const menuRef = useRef<HTMLDivElement>(undefined);

  const updatePosition = useCallback(() => {
    const trigger = ref.current;
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    setPosition({ top: rect.bottom + DROPDOWN_OFFSET_PX, left: rect.left });
  }, []);

  // Phone Number Revealed event tracking
  const { captureProCallIntent } = useAB();
  const user = useAppSelector(selectCurrentUser);
  const job = useAppSelector(state => jobSelectors.selectById(state, jobId));
  const category = useAppSelector(state => categoriesSelector.selectById(state, job?.relationships?.category?.data?.id));
  const businessUnit = useAppSelector(state => businessUnitsSelector.selectById(state, category?.relationships?.businessUnit?.data?.id));
  const municipality = useAppSelector(state => jobMunicipalitySelectors.selectById(state, job?.relationships?.municipality?.data?.id));
  const postingFlow = useAppSelector(state => state.pros.postingFlow);
  const countryCode = getCountryCode();

  const fireRevealEvent = () => {
    const isCleaning = parseInt(job?.relationships?.category?.data?.id, 0) === COUNTRY_CONFIG.CLEANING_CATEGORY[countryCode];
    const businessModel = job?.attributes?.pricingModel === JobPricingModels.MONEY_MODEL ? BusinessModel.MONEY : BusinessModel.CREDIT;

    // @AB PostHog event
    captureProCallIntent({
      category_slug: category?.attributes?.slug,
      sub_category_slug: job?.attributes?.subCategorySlug,
      business_unit_slug: businessUnit?.attributes?.slug,
      business_model: businessModel,
      municipality_slug: municipality?.attributes?.slug,
      county_slug: municipality?.attributes?.county.slug,
      job_posting_flow: postingFlow?.posting_flow,
      is_cleaning: isCleaning,
      is_b2b: job?.attributes?.isB2b ?? false,
      is_core: (!isCleaning && job?.attributes?.isB2b) ?? false,
      job_id: jobId,
      bid_id: bidId,
      pro_id: sellerId,
      customer_id: `${user?.id}`,
      source_phone_reveal: source
    });
  };

  const handleToggle = () => {
    if (isMobile) {
      fireRevealEvent();
      onReveal?.();
      window.location.href = `tel:${phone}`;

      return;
    }

    if (!open) {
      fireRevealEvent();
      updatePosition();
      onReveal?.();
    }
    setOpen(prev => !prev);
  };

  const copyPhoneFallback = (value: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const handleCopy = useCallback(() => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(phone);
    } else {
      copyPhoneFallback(phone);
    }
    setOpen(false);
    dispatch(addSnackbarNotification({
      type: SnackbarNotificationType.SUCCESS,
      message: t('T.PHONE-DROPDOWN.PHONE-COPIED')
    }));
  }, [phone]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  return (
    <div styleName="phone-dropdown-wrapper" ref={ref}>
      {React.cloneElement(children, { onClick: handleToggle })}
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          styleName="phone-dropdown"
          style={{ top: position.top, left: position.left }}
        >
          <a
            href={`tel:${phone}`}
            styleName="phone-dropdown__option"
            onClick={() => setOpen(false)}
          >
            {t('T.PHONE-DROPDOWN.CALL-{{phone}}', { phone })}
          </a>
          <button
            type="button"
            styleName="phone-dropdown__option phone-dropdown__option--copy"
            onClick={handleCopy}
          >
            {t('T.PHONE-DROPDOWN.COPY-PHONE')}
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};
